import { getNativePlatform } from '#features/notifications/utils/nativePlatform';
import { envConfig } from '#constants/envConfig';

/**
 * Google Play In-App Review для Android-оболочки (нативная часть —
 * `android/app/src/main/java/gg/teamplay/twa/StoreReviewPlugin.java`).
 *
 * Карточка оценки принадлежит Play: её нельзя вызвать кнопкой «оцените нас», нельзя ничего
 * спрашивать перед ней или во время, нельзя рисовать поверх и нельзя закрыть из кода. Всё, что
 * может приложение, — выбрать момент и попросить.
 *
 * Ответ `false` — это не ошибка, а весь класс ситуаций, где карточки не будет: веб и iOS,
 * эмулятор, отсутствующие Play Services, установка, которую Play не признаёт. Ответ `true`
 * означает ровно «Play принял просьбу» — не «человек увидел карточку» и тем более не «оценка
 * поставлена»: при исчерпанной квоте Play успешно не показывает ничего. Настоящая обратная
 * связь одна — счётчик оценок в Play Console.
 *
 * Отладочная сборка при этом карточку показать МОЖЕТ: проверено 26.08.2026 на Mi 10T Pro —
 * debug-подпись поверх опубликованного пакета, Play написал в лог `certificate mismatch` и всё
 * равно нарисовал настоящую карточку. Так что смоук по USB возможен; повторные прогоны за ним
 * упираются в квоту — Play отвечает успехом и не показывает ничего.
 */

interface StoreReviewPlugin {
  requestReview(): Promise<{ requested: boolean }>;
}

// undefined = плагин ещё не запрашивали; null = запрашивали, его нет (веб, iOS, старая сборка).
let cached: StoreReviewPlugin | null | undefined;
let loading: Promise<void> | null = null;

/**
 * Загружает прокси плагина ровно один раз и кладёт в `cached`. Возвращает `Promise<void>` и
 * НИКОГДА сам прокси: `registerPlugin` отдаёт Proxy, перехватывающий обращение к любому
 * свойству, включая `.then`. Стоит прокси стать значением, которым резолвится промис, — рантайм
 * примет его за thenable, дёрнет несуществующий нативный метод `then` и уронит вызов ещё до
 * моста: «StoreReview.then() is not implemented on android». Та же ловушка, что в
 * `playUpdate.ts` и `deviceSettings.ts`.
 */
function ensurePlugin(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = (async () => {
      try {
        // In-App Review — механизм Google Play. На iOS-оболочке его нет вовсе (там свой
        // SKStoreReviewController), грузить туда плагин незачем.
        if ((await getNativePlatform()) !== 'android') {
          cached = null;
          return;
        }
        const { registerPlugin } = await import('@capacitor/core');
        // Присваиваем прокси синхронно — не возвращаем и не await'им его (см. ловушку выше).
        cached = registerPlugin<StoreReviewPlugin>('StoreReview');
      } catch {
        cached = null;
      }
    })();
  }
  return loading;
}

/**
 * Просит Play показать карточку оценки.
 *
 * @returns приняли ли просьбу. Никогда не бросает: невозможность показать карточку — рабочее
 *          состояние, а не сбой для Sentry.
 */
export async function requestStoreReview(): Promise<boolean> {
  if (!envConfig.IS_NATIVE) {
    return false;
  }

  await ensurePlugin();

  if (!cached) {
    return false;
  }

  try {
    const { requested } = await cached.requestReview();
    return requested;
  } catch {
    return false;
  }
}
